/**
 * Media Entity Schemas
 *
 * Types for media library. Collection types are in collection.ts.
 */

import { z } from 'zod'

// Re-export collection types for backwards compatibility
export { MediaTypeSchema, type MediaType } from './collection'

/**
 * Media Status Schema
 */
export const MediaStatusSchema = z.enum(['PENDING', 'PROCESSING', 'READY', 'FAILED'])
export type MediaStatus = z.infer<typeof MediaStatusSchema>

/**
 * Media Schema
 */
export const MediaSchema = z.object({
  id: z.string(),
  mediaType: z.enum(['PHOTO', 'VIDEO', 'DOCUMENT', 'AUDIO']),
  status: MediaStatusSchema,
  filename: z.string(),
  originalFilename: z.string().optional(),
  mimeType: z.string(),
  size: z.number(),
  url: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  duration: z.number().nullable().optional(),
  caption: z.string().nullable().optional(),
  schoolId: z.string(),
  uploadedBy: z.string().nullable().optional(),
  capturedAt: z.coerce.date().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  // Mux video provider (new uploads). Both null for image rows and legacy Cloudinary video rows.
  muxAssetId: z.string().nullable().optional(),
  muxPlaybackId: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // Optional fields for backwards compatibility with web app components
  title: z.string().nullable().optional(),
  type: z.string().nullable().optional()
})

export type Media = z.infer<typeof MediaSchema>

/**
 * Media Report Status Schema
 */
export const MediaReportStatusSchema = z.enum(['PENDING', 'UNDER_REVIEW', 'REVIEWED', 'RESOLVED', 'DISMISSED', 'ALL'])
export type MediaReportStatus = z.infer<typeof MediaReportStatusSchema>

/**
 * Nested media info in reports
 */
export const ReportMediaInfoSchema = z.object({
  id: z.string(),
  url: z.string().nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  mediaType: z.enum(['PHOTO', 'VIDEO', 'DOCUMENT', 'AUDIO']).optional(),
  // Cloudinary URLs for display
  cloudinaryUrl: z.string().nullable().optional(),
  cloudinaryThumbnailUrl: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  uploadedBy: z
    .object({
      id: z.string(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      photoUrl: z.string().nullable().optional()
    })
    .nullable()
    .optional()
})

/**
 * Reporter user info
 */
export const ReporterInfoSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  photoUrl: z.string().nullable().optional()
})

/**
 * Media Report Schema
 */
/**
 * Resolution type for media reports
 */
export const MediaReportResolutionSchema = z.enum(['REMOVED', 'HIDDEN', 'NO_ACTION', 'WARNING_ISSUED'])
export type MediaReportResolution = z.infer<typeof MediaReportResolutionSchema>

/**
 * Media Report Schema
 */
export const MediaReportSchema = z.object({
  id: z.string(),
  mediaId: z.string(),
  reportedBy: z.union([z.string(), ReporterInfoSchema]).describe('Reporter ID or nested info'),
  reason: z.string(),
  description: z.string().nullable().optional(),
  status: MediaReportStatusSchema,
  resolution: MediaReportResolutionSchema.nullable().optional(),
  adminNotes: z.string().nullable().optional(),
  resolvedAt: z.coerce.date().nullable().optional(),
  resolvedBy: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  // Nested objects for display
  media: ReportMediaInfoSchema.optional(),
  reporter: ReporterInfoSchema.optional()
})

export type MediaReport = z.infer<typeof MediaReportSchema>
